import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Tabs, Tab } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CompanyService } from './service';
import { ErrorToast, SuccessToast } from '../../shared/toasters/toasters';
import DeleteModal from '../../Common/DeleteModal';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { User } from "../../context/AuthContext";
import { Switch } from "../../shared/components/Switch";
import Pagination from '../../Common/Pagination';
import CsvExportModal from '../../shared/components/CsvExportModal';

const CompanyList = () => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [term, setTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<number | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as 'all' | 'featured' | 'notFeatured') || 'all';

  const entriesPerPageOptions = [
    { label: '5', value: 5 },
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '30', value: 30 },
    { label: '40', value: 40 },
  ];

  const handleTermChange = (e: any) => {
    setTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleEntriesPerPageChange = (selectedOption: any) => {
    setEntriesPerPage(selectedOption ? selectedOption.value : 10);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleDeleteClick = (id: number) => {
    setCompanyToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (companyToDelete !== null) {
      try {
        await CompanyService.delete(companyToDelete);
        SuccessToast(t('company.successDelete'));
        setShowDeleteModal(false);
        setCompanyToDelete(null);
        fetchData();
      } catch (error) {
        ErrorToast(error);
      }
    }
  };

  const handleTabSelect = (k: string | null) => {
    setSearchParams({ tab: k || 'all' });
  };

  const handleToggleFeatured = async (id: number, isFeatured: boolean) => {
    try {
      await CompanyService.updateFeatured(id, { featured: isFeatured });
      SuccessToast(t("company.featuredUpdated"));
      fetchData();
    } catch (error) {
      ErrorToast(error);
    }
  };

  const fetchData = async () => {
    try {
      const response = await CompanyService.getAll({
        page: currentPage,
        perPage: entriesPerPage,
        term,
        featured: tab === 'featured' ? true : tab === 'notFeatured' ? false : undefined,
      });
      const data = response.data;
      setList(data);
      setTotalPages(response.totalPages);
    } catch (error) {
      ErrorToast(error);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [currentPage, entriesPerPage, term, tab]);

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody className="card-body">
            <div className="text-end">
              <button
                className="btn btn-success text-end me-2"
                onClick={() => setShowExportModal(true)}
              >
                <i className="ti ti-file-export f-18"></i> {t('csvExport.export')}
              </button>
              <button
                className="btn btn-primary text-end"
                onClick={() => navigate('/company/create')}
              >
                <i className="ti ti-plus f-18"></i> {t('company.addCompany')}
              </button>
            </div>

            <Tabs
              id="company-list-tabs"
              activeKey={tab}
              onSelect={handleTabSelect}
              className="mb-3"
            >
              <Tab eventKey="all" title={t('company.all')}>
                <CompanyTable
                  list={list}
                  t={t}
                  navigate={navigate}
                  handleDeleteClick={handleDeleteClick}
                  handleToggleFeatured={handleToggleFeatured}
                  handleTermChange={handleTermChange}
                  handleEntriesPerPageChange={handleEntriesPerPageChange}
                  entriesPerPageOptions={entriesPerPageOptions}
                  entriesPerPage={entriesPerPage}
                  term={term}
                />
              </Tab>
              <Tab eventKey="featured" title={t('company.featured')}>
                <CompanyTable
                  list={list}
                  t={t}
                  navigate={navigate}
                  handleDeleteClick={handleDeleteClick}
                  handleToggleFeatured={handleToggleFeatured}
                  handleTermChange={handleTermChange}
                  handleEntriesPerPageChange={handleEntriesPerPageChange}
                  entriesPerPageOptions={entriesPerPageOptions}
                  entriesPerPage={entriesPerPage}
                  term={term}
                />
              </Tab>
              {/* <Tab eventKey="notFeatured" title={t('company.featuredFalse')}>
                <CompanyTable
                  list={list}
                  t={t}
                  navigate={navigate}
                  handleDeleteClick={handleDeleteClick}
                  handleToggleFeatured={handleToggleFeatured}
                  handleTermChange={handleTermChange}
                  handleEntriesPerPageChange={handleEntriesPerPageChange}
                  entriesPerPageOptions={entriesPerPageOptions}
                  entriesPerPage={entriesPerPage}
                  term={term}
                />
              </Tab> */}
            </Tabs>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              previousLabel={t('company.previous')}
              nextLabel={t('company.next')}
            />
          </CardBody>
        </Card>
      </Col>

      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDeleteId={handleDeleteConfirm}
        name="company"
      />
      <CsvExportModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        title={t('csvExport.companiesTitle')}
        fileNamePrefix="companies"
        exportCsv={CompanyService.exportCsv}
      />
    </React.Fragment>
  );
};

// Extracted table for reuse in both tabs
const CompanyTable = ({
  list,
  t,
  navigate,
  handleDeleteClick,
  handleToggleFeatured,
  handleTermChange,
  handleEntriesPerPageChange,
  entriesPerPageOptions,
  entriesPerPage,
  term,
}: any) => (
  <>
    <div className="d-sm-flex align-items-center mb-3">
      <ul className="list-inline me-auto my-1">
        <li className="list-inline-item">
          <Select
            placeholder={t('company.entriesPerPage')}
            options={entriesPerPageOptions}
            onChange={handleEntriesPerPageChange}
            defaultValue={entriesPerPageOptions.find(
              (option: any) => option.value === entriesPerPage
            )}
            classNamePrefix="react-select"
          />
        </li>
        <span> {t('company.entriesPerPage')}</span>
      </ul>
      <ul className="list-inline ms-auto my-1 mt-3">
        <li className="list-inline-item">
          <form className="">
            <input
              type="search"
              className="form-control"
              placeholder={t('company.searchPlaceholder')}
              value={term}
              onChange={handleTermChange}
            />
          </form>
        </li>
      </ul>
    </div>
    <div className="table-responsive">
      <table className="table table-hover sticky-actions" id="pc-dt-simple-1">
        <thead>
          <tr>
            <th>{t('company.id')}</th>
            <th>{t('company.name')}</th>
            <th>{t('enums.userRole.ROLE_COMPANY_ADMIN')}</th>
            <th>{t('company.jobsCount')}</th>
            <th>{t('company.logo')}</th>
            <th>{t('company.createdAt')}</th>
            <th>{t('company.updatedAt')}</th>
            <th>{t("company.featured")}</th>
            <th className="text-end sticky-col">{t('company.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {list.map((item: any, index: number) => {
            const companyAdmin = item?.users?.find((user: User) =>
              user.roles.includes("ROLE_COMPANY_ADMIN")
            );
            return (
              <tr key={index}>
                <td>{item.id}</td>
                <td className="wrap-col">{item.name}</td>
                <td>
                  {companyAdmin?.firstName} {companyAdmin?.lastName}
                </td>
                <td>{item.jobsCount ?? '-'}</td>
                <td>
                  {item.logo ? (
                    <img
                      src={item.logo.path}
                      alt={item.name}
                      className="avatar-sm rounded bg-light img-fluid"
                      style={{ width: '75px', height: '50px', objectFit: 'contain' }}
                    />
                  ) : (
                    t('company.noLogo')
                  )}
                </td>
                <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                <td>{new Date(item.updatedAt).toLocaleDateString()}</td>
                <td className='text-center'>
                  <Switch
                    className='form-check form-switch'
                    id={`featured-${item.id}`}
                    checked={item.featured}
                    label=''
                    onChange={(checked: boolean) => handleToggleFeatured(item.id, checked)}
                  />
                </td>
                <td className="text-end text-nowrap sticky-col">
                  <ul className="list-inline mb-0">
                    <li
                      className="list-inline-item"
                      onClick={() => navigate(`/company/edit/${item.id}`)}
                    >
                      <a
                        href="#"
                        className="avtar avtar-s btn-link-success btn-pc-default"
                      >
                        <i className="ti ti-edit f-20"></i>
                      </a>
                    </li>
                    <li className="list-inline-item">
                      <span
                        role="button"
                        className="avtar avtar-s btn-link-danger btn-pc-default"
                        onClick={() => handleDeleteClick(item.id)}
                      >
                        <i className="ti ti-trash f-20"></i>
                      </span>
                    </li>
                  </ul>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </>
);

export default CompanyList;